import { useState, useMemo } from 'react';
import { useLogStore } from '@/stores/logStore';
import { Card } from '@/components/ui/card';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, BarChart, Bar } from 'recharts';
import { TrendingUp, Clock, Zap } from 'lucide-react';

export function PerformanceMetrics() {
  const { logs } = useLogStore();

  // Calculate execution time metrics
  const metrics = useMemo(() => {
    const now = new Date();
    const oneHourAgo = new Date(now.getTime() - 60 * 60 * 1000);
    const recentLogs = logs.filter((log) => new Date(log.timestamp) > oneHourAgo);

    const errorCount = recentLogs.filter((l) => l.level === 'error').length;
    const successCount = recentLogs.filter((l) => l.level === 'success').length;
    const totalCount = recentLogs.length;
    const successRate = totalCount > 0 ? Math.round((successCount / totalCount) * 100) : 0;

    // Group logs by hour
    const executionsByHour: Record<string, number> = {};
    recentLogs.forEach((log) => {
      const date = new Date(log.timestamp);
      const hour = date.getHours();
      const key = `${hour}:00`;
      executionsByHour[key] = (executionsByHour[key] || 0) + 1;
    });

    const chartData = Array.from({ length: 12 }, (_, i) => {
      const hour = (now.getHours() - 11 + i) % 24;
      const key = `${hour}:00`;
      return {
        time: key,
        executions: executionsByHour[key] || 0,
      };
    });

    return {
      totalExecutions: totalCount,
      successCount,
      errorCount,
      successRate,
      chartData,
    };
  }, [logs]);

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold text-foreground mb-2">Performance Metrics</h3>
        <p className="text-sm text-muted-foreground">Monitor workflow execution performance over time</p>
      </div>

      {/* Key Metrics */}
      <div className="grid md:grid-cols-4 gap-4">
        <Card className="p-4 space-y-2">
          <div className="flex items-center justify-between">
            <p className="text-sm text-muted-foreground">Total Executions</p>
            <Zap className="w-4 h-4 text-primary" />
          </div>
          <p className="text-2xl font-bold text-foreground">{metrics.totalExecutions}</p>
          <p className="text-xs text-muted-foreground">Last 60 minutes</p>
        </Card>

        <Card className="p-4 space-y-2 bg-green-500/5 border-green-500/20">
          <div className="flex items-center justify-between">
            <p className="text-sm text-muted-foreground">Successful</p>
            <TrendingUp className="w-4 h-4 text-green-600" />
          </div>
          <p className="text-2xl font-bold text-green-600">{metrics.successCount}</p>
          <p className="text-xs text-muted-foreground">{metrics.successRate}% success rate</p>
        </Card>

        <Card className="p-4 space-y-2 bg-destructive/5 border-destructive/20">
          <div className="flex items-center justify-between">
            <p className="text-sm text-muted-foreground">Failed</p>
            <TrendingUp className="w-4 h-4 text-destructive" />
          </div>
          <p className="text-2xl font-bold text-destructive">{metrics.errorCount}</p>
          <p className="text-xs text-muted-foreground">Requires attention</p>
        </Card>

        <Card className="p-4 space-y-2">
          <div className="flex items-center justify-between">
            <p className="text-sm text-muted-foreground">Avg Success Rate</p>
            <Clock className="w-4 h-4 text-primary" />
          </div>
          <p className="text-2xl font-bold text-foreground">{metrics.successRate}%</p>
          <p className="text-xs text-muted-foreground">Of all executions</p>
        </Card>
      </div>

      {/* Execution Timeline Chart */}
      <Card className="p-6">
        <h4 className="font-semibold text-foreground mb-4">Execution Timeline (Last 12 Hours)</h4>
        <ResponsiveContainer width="100%" height={300}>
          <BarChart data={metrics.chartData} margin={{ top: 5, right: 30, left: 0, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="currentColor" opacity={0.1} />
            <XAxis dataKey="time" stroke="currentColor" opacity={0.5} />
            <YAxis stroke="currentColor" opacity={0.5} />
            <Tooltip
              contentStyle={{
                backgroundColor: 'hsl(var(--background))',
                border: '1px solid hsl(var(--border))',
              }}
            />
            <Bar dataKey="executions" fill="hsl(var(--primary))" radius={[8, 8, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </Card>

      {/* Status Distribution */}
      <div className="grid md:grid-cols-2 gap-6">
        <Card className="p-6">
          <h4 className="font-semibold text-foreground mb-4">Execution Status</h4>
          <div className="space-y-3">
            <div className="flex items-center justify-between p-3 rounded bg-muted/50">
              <span className="text-sm text-muted-foreground">Successful</span>
              <div className="flex items-center gap-2">
                <div className="h-2 w-16 bg-green-500 rounded" />
                <span className="text-sm font-semibold">{metrics.successCount}</span>
              </div>
            </div>
            <div className="flex items-center justify-between p-3 rounded bg-muted/50">
              <span className="text-sm text-muted-foreground">Failed</span>
              <div className="flex items-center gap-2">
                <div className="h-2 w-16 bg-destructive rounded" />
                <span className="text-sm font-semibold">{metrics.errorCount}</span>
              </div>
            </div>
            <div className="flex items-center justify-between p-3 rounded bg-muted/50">
              <span className="text-sm text-muted-foreground">Total</span>
              <span className="text-sm font-semibold">{metrics.totalExecutions}</span>
            </div>
          </div>
        </Card>

        <Card className="p-6 bg-blue-500/5 border-blue-500/20">
          <h4 className="font-semibold text-foreground mb-4">Insights</h4>
          <div className="space-y-3 text-sm text-muted-foreground">
            {metrics.successRate === 100 ? (
              <p>✓ All executions are successful! Keep up the great work.</p>
            ) : metrics.successRate >= 90 ? (
              <p>✓ High success rate! Check the failed executions for improvements.</p>
            ) : metrics.successRate >= 70 ? (
              <p>⚠ Success rate could be improved. Review error logs for issues.</p>
            ) : (
              <p>✗ Low success rate. Check validation and configuration.</p>
            )}
            {metrics.totalExecutions === 0 && <p>No executions yet. Run a workflow to see metrics.</p>}
          </div>
        </Card>
      </div>
    </div>
  );
}
